import { Controller, useFormContext } from 'react-hook-form';
import Switch from '@/components/UI/FormElements/Switch';
import { ErrorMessage } from '@hookform/error-message';
import type { FormValues } from '../SingleSignOn';
import type { BaseSsoProviderProps, SsoProviderResponse } from '../SingleSignOn.types';
import { _isNotBlank, _notNil } from '@/littledash';
import { type FC, useEffect, useState } from 'react';
import { ResponseType } from '@/support/LegacyApiService';
import Loading from '@/components/Loading';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';

const Okta: FC<BaseSsoProviderProps> = ({ onLoadingChange, onSavedEnabledChange }) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const { response, loading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team.sso_providers',
    path: { type: 'okta' },
    options: { onError: { toast: false, throw: false, capture: true, slug: 'okta-sso-load-config' } },
  });

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (response?.type === ResponseType.Success) {
      const body = response.body as SsoProviderResponse;
      const data = body?.data;
      if (_notNil(data) && data.type === 'okta') {
        setValue('okta', {
          enabled: data.enabled ?? false,
          domain: data.settings?.domain ?? '',
          client_id: data.settings?.client_id ?? '',
          client_secret: data.settings?.client_secret ?? '',
        });
        setHasExistingConfig(_isNotBlank(data.settings?.client_id));
        onSavedEnabledChange?.(data.enabled ?? false);
      }
    }
  }, [response, setValue, onSavedEnabledChange]);

  if (loading) {
    return (
      <div className="ph3 pv4">
        <Loading />
      </div>
    );
  }

  return (
    <div className="ph3">
      <div className="mb3">
        <label>Enable Okta SSO</label>
        <Controller
          name="okta.enabled"
          defaultValue={false}
          control={control}
          render={({ field: { value, onChange } }) => <Switch value={value} onChange={onChange} />}
        />
      </div>
      <div className="mb3">
        <label htmlFor="okta.domain">Domain</label>
        <input
          {...register('okta.domain', { required: 'This field is required' })}
          type="text"
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="okta.domain"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3">
        <label htmlFor="okta.client_id">Client ID</label>
        <input
          {...register('okta.client_id', { required: 'This field is required' })}
          type="text"
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="okta.client_id"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3">
        <label htmlFor="okta.client_secret">Client secret</label>
        <input
          type="password"
          {...register('okta.client_secret', { required: 'This field is required' })}
          placeholder={hasExistingConfig ? '✳︎'.repeat(40) : ''}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="okta.client_secret"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
    </div>
  );
};

export default Okta;
